import { FC, useMemo } from "react";
import { faCodeCommit } from "@fortawesome/pro-solid-svg-icons";
import { faTicket } from "@fortawesome/pro-solid-svg-icons";
import { Group } from "@mantine/core";

import { Issue } from "@protos/issues/v1/issues";
import {
  BranchIcon,
  CodeLocationIcon,
  HoverCopyButton,
  ReachabilityIcon,
  RepoIcon,
  SeverityCardTimestamp,
  TransitivityIcon,
} from "@shared/components";
import { formatProviderRef, getSanitizedUrl } from "@shared/utils";

import { getGitProviderLinks } from "../../utils";
export interface ScaFindingInfoIconListProps {
  finding: Issue;
}

export const ScaFindingInfoIconList: FC<ScaFindingInfoIconListProps> = ({
  finding,
}) => {
  const { ref: refName, refUrl, provider } = getGitProviderLinks(finding);
  const repoUrl = useMemo(
    () => getSanitizedUrl(finding.lastSeenScan?.meta?.repo_url),
    [finding.lastSeenScan?.meta?.repo_url]
  );
  const formattedFindingBranches = finding.relatedIssues.map((branch) =>
    formatProviderRef(provider, branch.ref, +branch.pullRequestId)
  );
  const branchesCountText =
    finding.relatedIssues.length > 0 ? `+${finding.relatedIssues.length}` : "";
  const commitHash = finding.lastSeenScan?.meta?.commit ?? "";
  const externalTicket = finding.externalTicket;
  const ticketAttempts = finding.ticketAttempts;
  let errorMessage = finding.ticketAttempts[0]?.responseMessage;
  if (errorMessage) {
    if (errorMessage.includes("Client Error")) {
      errorMessage =
        "400 Bad Request. Please check your configuration settings.";
    }
    errorMessage = `Ticket creation failed with message: ${errorMessage}`;
  }

  return (
    <Group gap="lg" wrap="nowrap" style={{ rowGap: 8 }} fz="sm">
      {finding.relevantSince && (
        <SeverityCardTimestamp
          date={finding.relevantSince}
          hasComments={false} // TODO: Expose review_count, triage_count and all_refs in the Issue proto
          isBlocking={finding.isBlocking ?? false}
          fontSize="sm"
          tooltipWithArrow={false}
          tooltipPosition="bottom"
        />
      )}
      {finding.scaAttributes?.reachability && (
        <ReachabilityIcon
          reachability={finding.scaAttributes.reachability}
          reachableCondition={finding.scaAttributes.reachableCondition}
        />
      )}
      {finding.scaAttributes?.foundDependency?.transitivity && (
        <TransitivityIcon
          transitivity={finding.scaAttributes.foundDependency.transitivity}
        />
      )}
      {finding.repository && (
        <RepoIcon
          repoName={finding.repository.name}
          repoHref={repoUrl}
          repoProvider={provider}
        />
      )}
      {refName && (
        <BranchIcon
          branchName={`${refName} ${branchesCountText}`}
          branchHref={refUrl}
          allOtherBranchNames={formattedFindingBranches}
        />
      )}
      <HoverCopyButton
        copyText={commitHash}
        tooltipText="Copy full commit ID"
        iconSize="sm"
      >
        <CodeLocationIcon
          icon={faCodeCommit}
          label={commitHash.slice(0, 7)}
          href={finding.commitUrl}
        />
      </HoverCopyButton>
      {/* Ticket icon */}
      {externalTicket ? (
        <CodeLocationIcon
          label={externalTicket.externalSlug}
          href={externalTicket.url}
          icon={faTicket}
        />
      ) : ticketAttempts.length > 0 ? (
        <CodeLocationIcon
          label="Failed"
          icon={faTicket}
          color="var(--mantine-color-red-7)"
          onHoverText={errorMessage}
        />
      ) : null}
    </Group>
  );
};
